import Vue, { markRaw } from "vue";
import { Clerk } from "@clerk/clerk-js";
import { registerSession, resumeSession, clearSession } from "../services/session_manager";
import { showSessionAlert } from "../services/session_alert";

// The browser bundle (clerk.browser.js) includes UI components.
// If webpack resolves the ESM entry instead, Clerk won't have UI.
const ClerkConstructor = Clerk.Clerk || Clerk;

let instance;

const STORAGE_KEY = "tc_clerk_session";

const CLERK_PUBLISHABLE_KEY =
  process.env.VUE_APP_CLERK_PUBLISHABLE_KEY || "";

export const getClerkInstance = () => instance;

export const useClerkAuth = () => {
  if (instance) return instance;

  const saved = localStorage.getItem(STORAGE_KEY);
  let restored = {};
  if (saved) {
    try { restored = JSON.parse(saved); }
    catch (e) { localStorage.removeItem(STORAGE_KEY); }
  }

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: null,
        clerkInstance: null,
        error: null,
        _initPromise: null,
        _sessionRegistered: false,
        subscriptionValid: false,
        subscriptionChecked: false,
        subscriptionDetails: null,
        subscriptionPlan: restored.subscriptionPlan || null,
        subscriptionExpiry: restored.subscriptionExpiry || null,
      };
    },
    methods: {
      _persist() {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          isAuthenticated: this.isAuthenticated,
          user: this.user,
          subscriptionValid: this.subscriptionValid,
          subscriptionPlan: this.subscriptionPlan,
          subscriptionExpiry: this.subscriptionExpiry,
        }));
      },

      async init() {
        if (this._initPromise) return this._initPromise;
        this._initPromise = this._doInit();
        return this._initPromise;
      },

      async _doInit() {
        if (!CLERK_PUBLISHABLE_KEY) {
          console.error("[ClerkAuth] No publishable key. Set VUE_APP_CLERK_PUBLISHABLE_KEY in .env");
          this.loading = false;
          this.error = "Clerk publishable key not configured";
          return;
        }
        try {
          this.loading = true;
          const clerk = new ClerkConstructor(CLERK_PUBLISHABLE_KEY);
          await clerk.load();
          this.clerkInstance = markRaw(clerk);
          this._syncFromClerk();
          clerk.addListener(() => { this._syncFromClerk(); });
          if (this.isAuthenticated) {
            try { await this.refreshSubscription(); }
            catch (error) { this.error = 'Unable to verify subscription. Please retry from your dashboard.'; }
          }
          this.loading = false;
        } catch (err) {
          console.error("[ClerkAuth] init error:", err);
          this.error = err.message || "Failed to initialise authentication";
          this.loading = false;
        }
      },

      async _ensureLoaded() {
        if (this.clerkInstance) return this.clerkInstance;
        if (this._initPromise) await this._initPromise;
        return this.clerkInstance;
      },

      _syncFromClerk() {
        const clerk = this.clerkInstance;
        if (!clerk) return;
        const session = clerk.session;
        const user = clerk.user;
        if (session && user) {
          // Cached identity only chooses resume versus registration after Clerk
          // has verified the current session; it never grants authentication.
          if (this.user && this.user.id !== user.id) {
            this.subscriptionChecked = false;
            this.subscriptionDetails = null;
            this.clearSubscription();
          }
          const wasAuthenticated = this.isAuthenticated ||
            (restored.isAuthenticated && restored.user?.id === user.id);
          restored = {};
          this.isAuthenticated = true;
          this.user = {
            id: user.id,
            email: user.primaryEmailAddress?.emailAddress || user.emailAddresses?.[0]?.emailAddress || "",
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            fullName: user.fullName || "",
            imageUrl: user.imageUrl || "",
          };
          // Close the auth overlay if open (user just signed in)
          this._unmountClerkComponent();

          // Sync user identity to server database
          this._syncUserToServer();

          // Session enforcement:
          // - If user was NOT authenticated before (fresh login) → register new session (kicks other devices)
          // - If user WAS already authenticated (page refresh/restore) → validate existing session
          if (!this._sessionRegistered) {
            this._sessionRegistered = true;
            if (!wasAuthenticated) {
              // Fresh login — register and kick other sessions
              this._registerSession();
            } else {
              // Page refresh — just start validation polling without re-registering
              this._startSessionValidation();
            }
          }
        } else {
          this.isAuthenticated = false;
          this.subscriptionChecked = false;
          this.subscriptionDetails = null;
          this.user = null;
          this.subscriptionValid = false;
          this.subscriptionPlan = null;
          this.subscriptionExpiry = null;
          this._sessionRegistered = false;
        }
        this._persist();
      },

      /**
       * Register this session with the server for single-session enforcement.
       * Called once after a fresh login (not on page refresh).
       */
      async _registerSession() {
        try {
          await registerSession(
            () => this.getToken(),
            (message) => this._handleForceLogout(message),
            "web"
          );
        } catch (err) {
          console.warn("[ClerkAuth] Session registration failed:", err.message);
        }
      },

      /**
       * Sync user identity (email, name, avatar) to the server database.
       * Called on every login/page load so the DB always has fresh data.
       */
      async _syncUserToServer() {
        if (!this.user || !this.user.id) return;
        try {
          const token = await this.getToken();
          if (!token) return;
          const API_BASE = process.env.VUE_APP_API_BASE || '';
          await fetch(`${API_BASE}/api/user/sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: this.user.email || '',
              firstName: this.user.firstName || '',
              lastName: this.user.lastName || '',
              avatarUrl: this.user.imageUrl || '',
            }),
          });
        } catch (err) {
          // Non-critical — don't block the user
          console.warn("[ClerkAuth] User sync failed:", err.message);
        }
      },

      /**
       * Start session validation without re-registering.
       * Used on page refresh when the user already has a session.
       * If the stored session was invalidated, forces logout.
       */
      async _startSessionValidation() {
        try {
          await resumeSession(
            () => this.getToken(),
            (message) => this._handleForceLogout(message),
            "web"
          );
        } catch (err) {
          console.warn("[ClerkAuth] Session validation start failed:", err.message);
        }
      },

      /**
       * Handle forced logout when another session takes over.
       */
      _handleForceLogout(message) {
        console.warn("[ClerkAuth] Force logout:", message);
        const msg = message || "Your session was ended because you logged in from another device.";
        // Show custom alert popup, then logout and redirect
        showSessionAlert(msg).then(async () => {
          await this.logout();
          window.location.href = "/";
        });
      },

      async refreshSubscription() {
        if (this._subscriptionPromise) return this._subscriptionPromise;
        this._subscriptionPromise = (async () => {
          const userId = this.user?.id;
          const token = await this.getToken();
          if (!token || !userId) throw new Error('Sign in to check subscription.');
          const response = await fetch(`${process.env.VUE_APP_API_BASE || ''}/api/subscription/status`, {
            headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(10000),
          });
          if (!response.ok) throw new Error('Subscription check failed.');
          const data = await response.json();
          if (typeof data.active !== 'boolean') throw new Error('Invalid subscription response.');
          if (userId !== this.user?.id) throw new Error('Account changed during subscription check.');
          this.subscriptionDetails = data;
          if (data.active) this.setSubscription(data.plan, data.expiresAt);
          else this.clearSubscription();
          this.subscriptionChecked = true;
          return data;
        })().finally(() => { this._subscriptionPromise = null; });
        return this._subscriptionPromise;
      },

      async openSignIn() {
        window.location.assign('https://accounts.tradingcandle.com/sign-in?redirect_url=' +
          encodeURIComponent(window.location.origin + '/chart'));
      },

      async openSignUp() {
        window.location.assign('https://accounts.tradingcandle.com/sign-up?redirect_url=' +
          encodeURIComponent(window.location.origin + '/chart'));
      },

      _unmountClerkComponent() {
        // No-op: kept for backward compatibility with _syncFromClerk
      },

      async logout() {
        this._unmountClerkComponent();
        // Clear session on server before signing out
        try {
          await clearSession();
        } catch (err) {
          console.warn("[ClerkAuth] Failed to clear session:", err.message);
        }
        const clerk = await this._ensureLoaded();
        if (clerk) await clerk.signOut();
        this.isAuthenticated = false;
        this.user = null;
        this.subscriptionValid = false;
        this.subscriptionPlan = null;
        this.subscriptionExpiry = null;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("tc_auth_session");
        localStorage.removeItem("tc_pending_session");
        localStorage.removeItem("tc_auth_result");
      },

      setSubscription(plan, expiryDate) {
        this.subscriptionValid = true;
        this.subscriptionPlan = plan;
        this.subscriptionExpiry = expiryDate;
        this._persist();
      },

      clearSubscription() {
        this.subscriptionValid = false;
        this.subscriptionPlan = null;
        this.subscriptionExpiry = null;
        this._persist();
      },

      isSubscriptionActive() {
        if (!this.subscriptionValid || !this.subscriptionExpiry) return false;
        return new Date(this.subscriptionExpiry) > new Date();
      },

      async getToken() {
        const clerk = await this._ensureLoaded();
        
        if (!clerk?.session) {
          return null;
        }
        
        const token = clerk.session.getToken();
        return token;
      },

      async getPublicMetadata() {
        const clerk = await this._ensureLoaded();
        if (!clerk?.user) return {};
        return clerk.user.publicMetadata || {};
      },

      async updatePublicMetadata(metadata) {
        const clerk = await this._ensureLoaded();
        if (!clerk?.user) {
          throw new Error('User not authenticated');
        }
        
        await clerk.user.update({
          publicMetadata: {
            ...clerk.user.publicMetadata,
            ...metadata
          }
        });
        
        return true;
      },

      async getUnsafeMetadata() {
        const clerk = await this._ensureLoaded();
        if (!clerk?.user) return {};
        return clerk.user.unsafeMetadata || {};
      },

      async updateUnsafeMetadata(metadata) {
        const clerk = await this._ensureLoaded();
        if (!clerk?.user) {
          throw new Error('User not authenticated');
        }
        
        await clerk.user.update({
          unsafeMetadata: {
            ...clerk.user.unsafeMetadata,
            ...metadata
          }
        });
        
        return true;
      },
    },
  });

  return instance;
};

export const ClerkAuthPlugin = {
  install(VueConstructor) {
    const auth = useClerkAuth();
    const globals=VueConstructor.config?.globalProperties || VueConstructor.prototype;
    globals.$clerk = auth;
    globals.$qrAuth = {
      get isAuthenticated() { return auth.isAuthenticated; },
      get subscriptionValid() { return auth.subscriptionValid; },
      get user() { return auth.user; },
      _applyAuth() {},
      logout() { return auth.logout(); },
    };
  },
};
